import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { pick } from 'lodash';
import { useDispatch } from 'react-redux';
import { communicationsMethods } from 'const';
import api from 'client';
import { formatters } from 'utils';
import { fetchAccount } from 'store/actions/account';
import useAuth from './useAuth';

export default () => {
  const { id } = useAuth();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { data, isLoading, isSuccess } = useQuery(['GET', `/apis/v2/patients/${id}/profile`, {}], { suspense: true });
  const attributes = data?.data?.attributes ?? null;

  const profile = useMemo(() => {
    if (attributes) {
      const prof = { ...attributes };
      const arr = [];

      if (prof.isSmsConsented) arr.push(communicationsMethods.textSms);
      if (prof.isEmailConsented) arr.push(communicationsMethods.email);
      if (prof.isCallConsented) arr.push(communicationsMethods.voice);

      prof.preferredCommunication = arr;

      if (prof.homeNumber) prof.homeNumber = formatters.parseNumber(prof.homeNumber);
      if (prof.mobileNumber) prof.mobileNumber = formatters.parseNumber(prof.mobileNumber);

      return prof;
    }
    return attributes;
  }, [attributes]);

  const mutation = useMutation(
    async ({ preferredCommunication, preferredName, homeNumber, homeAddress, aptSuiteUnit, ...newData }) => {
      const newProfile = { ...profile, ...newData };

      if (preferredCommunication) {
        newProfile.isSmsConsented = preferredCommunication.includes(communicationsMethods.textSms);
        newProfile.isEmailConsented = preferredCommunication.includes(communicationsMethods.email);
        newProfile.isCallConsented = preferredCommunication.includes(communicationsMethods.voice);
      }

      newProfile.aptSuiteUnit = aptSuiteUnit;
      newProfile.homePhoneNumber = homeNumber;
      newProfile.preferredName = preferredName;
      newProfile.homePhoneNumberId = newProfile.homeNumberId;
      newProfile.isHomePrimary = false;
      newProfile.mobilePhoneNumber = newProfile.mobileNumber;
      newProfile.mobilePhoneNumberId = newProfile.mobileNumberId;
      newProfile.isMobilePrimary = true;
      newProfile.qualifiedStatus = 'none';
      newProfile.locationId = null;
      newProfile.externalProviderId = null;

      if (homeAddress) {
        if (homeAddress.description) newProfile.formattedAddress = homeAddress.description;
        else if (typeof homeAddress === 'string') newProfile.formattedAddress = homeAddress;
      }

      return api.put(
        `/apis/v2/patient_profiles/${id}`,
        pick(newProfile, [
          'email',
          'firstName',
          'lastName',
          'preferredName',
          'healthCardNumber',
          'versionCode',
          'formattedAddress',
          'veteranId',
          'salutation',
          'language',
          'mobilePhoneNumber',
          'mobilePhoneNumberId',
          'isMobilePrimary',
          'homePhoneNumber',
          'homePhoneNumberId',
          'qualifiedStatus',
          'gender',
          'province',
          'locationId',
          'externalProviderId',
          'pharmacyId',
          'birthDate',
          'isSmsConsented',
          'isEmailConsented',
          'isCallConsented',
          'timezone',
          'alert',
          'aptSuiteUnit',
          'referralSource',
          'personalPronoun',
        ]),
      );
    },
    {
      onSuccess: async () => {
        await dispatch(fetchAccount(true, () => queryClient.removeQueries(() => true)));
      },
      onError: (error) => {
        throw error;
      },
    },
  );

  return {
    updateUserProfile: mutation.mutateAsync,
    data: profile,
    isLoading,
    isSuccess,
  };
};
